import { useMounted } from '@hooks/useMounted';
import { useCallback, useEffect, useState } from 'react';
import { call } from '../../functions';

type PromiseArgument = ((...argv: any[]) => undefined | Promise<any>) | undefined | Promise<any>;

export interface UseLoadingType {
    loading: boolean;
    state: boolean;

    setState(state: boolean): void;

    start(): void;

    stop(): void;

    fromPromise(promise: PromiseArgument): Promise<any>;
}

export const useLoading = (status = false, func?: () => void): UseLoadingType => {
    const [loading, setLoading] = useState(status);
    const isMounted = useMounted();

    const setState = useCallback(
        (state: boolean) => {
            if (isMounted()) {
                setLoading(state);
            }
        },
        [isMounted, setLoading],
    );

    const start = useCallback(() => {
        setState(true);
    }, [setState]);

    const stop = useCallback(() => {
        setState(false);
    }, [setState]);

    /**
     * @usage
     * const loading = useLoading();
     * const data = await loading.fromPromise(api.get(...));
     *
     * // Instead
     * const loading = useLoading();
     * loading.start();
     * try {
     *     const data = await api.get(...);
     * } catch (e) {
     *     // console.log(e)
     * }
     * loading.stop();
     *
     */
    const fromPromise = useCallback(
        (promise: PromiseArgument, ...args: any[]): Promise<any> => {
            if (!loading) {
                start();
            }

            try {
                if (typeof promise === 'function') {
                    promise = promise(...args);
                }

                if (promise instanceof Promise) {
                    return promise.catch(stop).finally(stop);
                } else {
                    stop();
                }
            } catch (e) {
                stop();
            }

            return new Promise((resolve) => setTimeout(resolve));
        },
        [start, stop],
    );

    useEffect(() => {
        call(func, loading);
    }, [loading]);

    useEffect(() => {
        setState(status);
    }, [status]);

    return {
        loading,
        start,
        stop,
        fromPromise,
        setState,
        state: loading,
    };
};
